import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Tabs,
  Tab,
  TextField,
  Button,
  Alert,
  Snackbar,
} from '@mui/material';
import ChildCard from '@/components/shared/ChildCard';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`credentials-tabpanel-${index}`}
      aria-labelledby={`credentials-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const CredentialsPage: React.FC = () => {
  const { t } = useTranslation();
  const [value, setValue] = useState(0);
  const [credentials, setCredentials] = useState<any>({});
  const [credentialsExists, setCredentialsExists] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    const loadCredentials = async () => {
      const data = await apiService.getCredentials();
      setCredentials(data);
      // Якщо дані порожні (404) - credentials не існують
      const exists = Object.keys(data).length > 0;
      setCredentialsExists(exists);

      // Показуємо інформаційний алерт якщо credentials не встановлені
      if (!exists) {
        setAlertMessage({ type: 'info', message: t('credentials.notSet') });
      }
    };

    loadCredentials();
  }, []);

  const handleSavePayPal = async () => {
    try {
      setAlertMessage(null);
      const paypalData: any = {};
      if (credentials.paypalClientId !== undefined)
        paypalData.paypalClientId = credentials.paypalClientId;
      if (credentials.paypalClientSecret !== undefined)
        paypalData.paypalClientSecret = credentials.paypalClientSecret;

      await apiService.updateCredentials(paypalData, !credentialsExists);
      if (!credentialsExists) setCredentialsExists(true);
      setAlertMessage({
        type: 'success',
        message: t('credentials.messages.paypalSaved'),
      });
    } catch (error) {
      console.error('Error saving PayPal credentials:', error);
      setAlertMessage({
        type: 'error',
        message: t('credentials.errors.paypalError'),
      });
    }
  };

  const handleSaveEVC = async () => {
    try {
      setAlertMessage(null);
      const evcData: any = {};
      if (credentials.evcUsername !== undefined)
        evcData.evcUsername = credentials.evcUsername;
      if (credentials.evcPassword !== undefined)
        evcData.evcPassword = credentials.evcPassword;

      await apiService.updateCredentials(evcData, !credentialsExists);
      if (!credentialsExists) setCredentialsExists(true);
      setAlertMessage({
        type: 'success',
        message: t('credentials.messages.evcSaved'),
      });
    } catch (error) {
      console.error('Error saving EVC credentials:', error);
      setAlertMessage({
        type: 'error',
        message: t('credentials.errors.evcError'),
      });
    }
  };

  const handleSaveAutoFlasher = async () => {
    try {
      setAlertMessage(null);
      const autoFlasherData: any = {};
      if (credentials.autoflasherApiKey !== undefined)
        autoFlasherData.autoflasherApiKey = credentials.autoflasherApiKey;

      await apiService.updateCredentials(autoFlasherData, !credentialsExists);
      if (!credentialsExists) setCredentialsExists(true);
      setAlertMessage({
        type: 'success',
        message: t('credentials.messages.autoFlasherSaved'),
      });
    } catch (error) {
      console.error('Error saving AutoFlasher credentials:', error);
      setAlertMessage({
        type: 'error',
        message: t('credentials.errors.autoFlasherError'),
      });
    }
  };

  const handleSaveDTC = async () => {
    try {
      setAlertMessage(null);
      const dtcData: any = {};
      if (credentials.dtcApiKey !== undefined)
        dtcData.dtcApiKey = credentials.dtcApiKey;

      await apiService.updateCredentials(dtcData, !credentialsExists);
      if (!credentialsExists) setCredentialsExists(true);
      setAlertMessage({
        type: 'success',
        message: t('credentials.messages.dtcSaved'),
      });
    } catch (error) {
      console.error('Error saving DTC credentials:', error);
      setAlertMessage({
        type: 'error',
        message: t('credentials.errors.dtcError'),
      });
    }
  };

  const handleSaveFlexSlave = async () => {
    try {
      setAlertMessage(null);
      const flexSlaveData: any = {};
      if (credentials.flexSlaveApiKey !== undefined)
        flexSlaveData.flexSlaveApiKey = credentials.flexSlaveApiKey;

      await apiService.updateCredentials(flexSlaveData, !credentialsExists);
      if (!credentialsExists) setCredentialsExists(true);
      setAlertMessage({
        type: 'success',
        message: t('credentials.messages.flexSlaveSaved'),
      });
    } catch (error) {
      console.error('Error saving FlexSlave credentials:', error);
      setAlertMessage({
        type: 'error',
        message: t('credentials.errors.flexSlaveError'),
      });
    }
  };

  const handleSaveSMTP = async () => {
    try {
      setAlertMessage(null);
      const smtpData: any = {};
      if (credentials.smtpHost !== undefined)
        smtpData.smtpHost = credentials.smtpHost;
      if (credentials.smtpPort !== undefined)
        smtpData.smtpPort = credentials.smtpPort;
      if (credentials.smtpUsername !== undefined)
        smtpData.smtpUsername = credentials.smtpUsername;
      if (credentials.smtpPassword !== undefined)
        smtpData.smtpPassword = credentials.smtpPassword;
      if (credentials.smtpSenderEmail !== undefined)
        smtpData.smtpSenderEmail = credentials.smtpSenderEmail;
      if (credentials.smtpSenderName !== undefined)
        smtpData.smtpSenderName = credentials.smtpSenderName;
      if (credentials.smtpSupportEmail !== undefined)
        smtpData.smtpSupportEmail = credentials.smtpSupportEmail;

      await apiService.updateCredentials(smtpData, !credentialsExists);
      if (!credentialsExists) setCredentialsExists(true);
      setAlertMessage({
        type: 'success',
        message: t('credentials.messages.smtpSaved'),
      });
    } catch (error) {
      console.error('Error saving SMTP credentials:', error);
      setAlertMessage({
        type: 'error',
        message: t('credentials.errors.smtpError'),
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('credentials.title')}
      </Typography>

      {alertMessage && (
        <Snackbar
          open={!!alertMessage}
          autoHideDuration={6000}
          onClose={() => setAlertMessage(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            variant="filled"
            onClose={() => setAlertMessage(null)}
            severity={alertMessage.type}
            sx={{ width: '100%' }}
          >
            {alertMessage.message}
          </Alert>
        </Snackbar>
      )}

      <ChildCard>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="credentials tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mb: 3,
          }}
        >
          <Tab label={t('credentials.tabs.paypal')} />
          <Tab label={t('credentials.tabs.evc')} />
          <Tab label={t('credentials.tabs.autoFlasher')} />
          <Tab label={t('credentials.tabs.dtc')} />
          <Tab label={t('credentials.tabs.flexSlave')} />
          <Tab label={t('credentials.tabs.smtp')} />
        </Tabs>

        <TabPanel value={value} index={0}>
          <Box component="form" noValidate sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label={t('credentials.fields.clientId')}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={credentials.paypalClientId || ''}
                onChange={e =>
                  setCredentials({
                    ...credentials,
                    paypalClientId: e.target.value,
                  })
                }
              />
              <TextField
                fullWidth
                label={t('credentials.fields.clientSecret')}
                variant="outlined"
                type="password"
                InputLabelProps={{ shrink: true }}
                value={credentials.paypalClientSecret || ''}
                onChange={e =>
                  setCredentials({
                    ...credentials,
                    paypalClientSecret: e.target.value,
                  })
                }
              />
              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{ alignSelf: 'flex-start' }}
                onClick={handleSavePayPal}
              >
                {t('credentials.save')}
              </Button>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={value} index={1}>
          <Box component="form" noValidate sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label={t('credentials.fields.userName')}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={credentials.evcUsername || ''}
                onChange={e =>
                  setCredentials({
                    ...credentials,
                    evcUsername: e.target.value,
                  })
                }
              />
              <TextField
                fullWidth
                label={t('credentials.fields.password')}
                variant="outlined"
                type="password"
                InputLabelProps={{ shrink: true }}
                value={credentials.evcPassword || ''}
                onChange={e =>
                  setCredentials({
                    ...credentials,
                    evcPassword: e.target.value,
                  })
                }
              />
              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{ alignSelf: 'flex-start' }}
                onClick={handleSaveEVC}
              >
                {t('credentials.save')}
              </Button>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={value} index={2}>
          <Box component="form" noValidate sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label={t('credentials.fields.apiKey')}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={credentials.autoflasherApiKey || ''}
                onChange={e =>
                  setCredentials({
                    ...credentials,
                    autoflasherApiKey: e.target.value,
                  })
                }
              />
              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{ alignSelf: 'flex-start' }}
                onClick={handleSaveAutoFlasher}
              >
                {t('credentials.save')}
              </Button>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={value} index={3}>
          <Box component="form" noValidate sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label={t('credentials.fields.apiKey')}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={credentials.dtcApiKey || ''}
                onChange={e =>
                  setCredentials({ ...credentials, dtcApiKey: e.target.value })
                }
              />
              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{ alignSelf: 'flex-start' }}
                onClick={handleSaveDTC}
              >
                {t('credentials.save')}
              </Button>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={value} index={4}>
          <Box component="form" noValidate sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label={t('credentials.fields.apiKey')}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={credentials.flexSlaveApiKey || ''}
                onChange={e =>
                  setCredentials({
                    ...credentials,
                    flexSlaveApiKey: e.target.value,
                  })
                }
              />
              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{ alignSelf: 'flex-start' }}
                onClick={handleSaveFlexSlave}
              >
                {t('credentials.save')}
              </Button>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={value} index={5}>
          <Box component="form" noValidate sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <TextField
                  sx={{ flex: '1 1 0', minWidth: '200px' }}
                  label={t('credentials.fields.host')}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={credentials.smtpHost || ''}
                  onChange={e =>
                    setCredentials({ ...credentials, smtpHost: e.target.value })
                  }
                />
                <TextField
                  sx={{ flex: '1 1 0', minWidth: '200px' }}
                  label={t('credentials.fields.port')}
                  variant="outlined"
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  value={credentials.smtpPort || ''}
                  onChange={e =>
                    setCredentials({
                      ...credentials,
                      smtpPort: parseInt(e.target.value) || '',
                    })
                  }
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <TextField
                  sx={{ flex: '1 1 0', minWidth: '200px' }}
                  label={t('credentials.fields.userName')}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={credentials.smtpUsername || ''}
                  onChange={e =>
                    setCredentials({
                      ...credentials,
                      smtpUsername: e.target.value,
                    })
                  }
                />
                <TextField
                  sx={{ flex: '1 1 0', minWidth: '200px' }}
                  label={t('credentials.fields.password')}
                  variant="outlined"
                  type="password"
                  InputLabelProps={{ shrink: true }}
                  value={credentials.smtpPassword || ''}
                  onChange={e =>
                    setCredentials({
                      ...credentials,
                      smtpPassword: e.target.value,
                    })
                  }
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <TextField
                  sx={{ flex: '1 1 0', minWidth: '200px' }}
                  label={t('credentials.fields.email')}
                  variant="outlined"
                  type="email"
                  InputLabelProps={{ shrink: true }}
                  value={credentials.smtpSenderEmail || ''}
                  onChange={e =>
                    setCredentials({
                      ...credentials,
                      smtpSenderEmail: e.target.value,
                    })
                  }
                />
                <TextField
                  sx={{ flex: '1 1 0', minWidth: '200px' }}
                  label={t('credentials.fields.senderName')}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={credentials.smtpSenderName || ''}
                  onChange={e =>
                    setCredentials({
                      ...credentials,
                      smtpSenderName: e.target.value,
                    })
                  }
                />
              </Box>
              <TextField
                fullWidth
                label={t('credentials.fields.supportEmail')}
                variant="outlined"
                type="email"
                InputLabelProps={{ shrink: true }}
                value={credentials.smtpSupportEmail || ''}
                onChange={e =>
                  setCredentials({
                    ...credentials,
                    smtpSupportEmail: e.target.value,
                  })
                }
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleSaveSMTP}
                >
                  {t('credentials.save')}
                </Button>
              </Box>
            </Box>
          </Box>
        </TabPanel>
      </ChildCard>
    </Box>
  );
};

export default CredentialsPage;
